import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ConfirmDialog } from '../../../../../shared/sso-workspace/confirm-dialog'
import { STATE_ORDER, STATE_VIEW, entryPointWarning, leaveOnConfirm } from '../signin-method-words'

// A method's state as a three-way control (On / Temporarily unavailable /
// Off), bound to the screen's edits: the choice is saved with Save.
//
// Leaving On asks first. For a kind whose button must stay visible
// (entryPointRequiredBy: Microsoft's app gallery listing), choosing Off
// opens the warning instead (design §5): Mark unavailable (the primary
// choice), Switch off anyway (then the save carries confirmEntryPoint: auth
// refuses Off without it) or Cancel. A kind that must pass its check before
// it is switched on (Google, Apple, Facebook) says so while On is chosen.
//
// saved: the state as auth last answered it. value: the chosen one.
// onChoose(state, { confirmEntryPoint }) sets the choice.
export const MethodState = ({ method, label, saved, value, disabled, onChoose }) => {
  // null, or { next } (leaving On) or { entryPoint: true } (Off on a kind
  // whose button must stay visible)
  const [asking, setAsking] = useState(null)

  const choose = next => {
    if (disabled || next === value) return
    if (next === 'off' && method.entryPointRequiredBy && saved !== 'off') {
      setAsking({ entryPoint: true })
      return
    }
    if (value === 'on' && saved === 'on' && next !== 'on') {
      setAsking({ next })
      return
    }
    onChoose(next, { confirmEntryPoint: false })
  }

  const view = STATE_VIEW[value] || STATE_VIEW.off
  const confirm = asking && asking.next ? leaveOnConfirm(asking.next, label) : null

  return (
    <div className="sw-field" data-testid="method-state">
      <span className="sw-label d-block" id="method-state-label">State</span>
      <div className="sw-segmented" role="radiogroup" aria-labelledby="method-state-label" aria-describedby="method-state-sentence">
        {STATE_ORDER.map(state =>
          <button key={state} type="button" role="radio" aria-checked={value === state} className={`sw-segment ${value === state ? 'sw-on' : ''}`.trim()}
            disabled={disabled} onClick={() => choose(state)}>{STATE_VIEW[state].label}</button>)}
      </div>
      <div className="sw-hint" id="method-state-sentence" data-testid="method-state-sentence">
        {view.sentence}{value !== saved ? ' It takes effect when you save.' : ''}
      </div>
      {method.checkToSwitchOn && value === 'on' && saved !== 'on' &&
        <div className="sw-hint sw-warn mt-1" role="note">
          Switching it on runs its check first: until the check passes, it stays {(STATE_VIEW[saved] || STATE_VIEW.off).label.toLowerCase()}.
        </div>}

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm ? confirm.title : ''}
        body={confirm ? confirm.body : ''}
        confirmText={confirm ? confirm.confirmText : ''}
        confirmClassName={asking && asking.next === 'unavailable' ? 'btn sw-btn-primary' : undefined}
        onConfirm={() => { const next = asking.next; setAsking(null); onChoose(next, { confirmEntryPoint: false }) }}
        onCancel={() => setAsking(null)}
      />
      <EntryPointDialog
        open={Boolean(asking && asking.entryPoint)}
        label={label}
        requiredBy={method.entryPointRequiredBy}
        onUnavailable={() => { setAsking(null); onChoose('unavailable', { confirmEntryPoint: false }) }}
        onOff={() => { setAsking(null); onChoose('off', { confirmEntryPoint: true }) }}
        onCancel={() => setAsking(null)}
      />
    </div>
  )
}

// The warning before switching off a kind whose button must stay visible.
// ConfirmDialog's look and manners (modal, portalled, Escape and the
// backdrop are Cancel, focus starts on Cancel), with a third way out.
export const EntryPointDialog = ({ open, label, requiredBy, onUnavailable, onOff, onCancel }) => {
  useEffect(() => {
    if (!open) return undefined
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); onCancel() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  if (!open) return null

  return createPortal(
    <div className="sw-drawer justify-content-center align-items-center" onClick={e => { if (e.target === e.currentTarget) onCancel() }}>
      <div className="sw-dialog" role="alertdialog" aria-modal="true" aria-labelledby="sm-entry-point-title" aria-describedby="sm-entry-point-body">
        <h2 className="h5" id="sm-entry-point-title">{`Switch off ${label}?`}</h2>
        <p className="sw-hint" id="sm-entry-point-body">{entryPointWarning(requiredBy, label)}</p>
        <div className="sw-dialog-actions">
          <button type="button" className="btn sw-btn" autoFocus onClick={onCancel}>Cancel</button>
          <button type="button" className="btn sw-btn" onClick={onOff}>Switch off anyway</button>
          <button type="button" className="btn sw-btn-primary" onClick={onUnavailable}>Mark unavailable</button>
        </div>
      </div>
    </div>,
    document.body
  )
}
